import { AuthTokensRepository } from '@foundation/auth/state';
import { buildAdminRoute } from '@foundation/admin/pages';
import { adminAndVerifiedGuard } from '@foundation/shared/access';
import { InterviewResolver } from '@spoken/interviews/resolvers';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegmentGroup } from '@angular/router';

import { UrlMatchResult, UrlSegment } from '@angular/router';
import { environment } from '../environments/environment';

const cnamed = !(window.location.origin.includes('localhost') || window.location.origin.startsWith('https://spoken.systems'));
console.log('[appRoutes] cnamed', cnamed);

/**
 * Generate article routes for the given path and source folder ID.
 * @param path
 * @param sourceFolderId
 * @param listName
 * @param listDescription
 * @returns
 */
function generateArticleRoutes(path: string, sourceFolderId: string, listName?: string, listDescription?: string, commentTitle?: string): Route {
	function prefixMatcher(segments: UrlSegment[]): UrlMatchResult | null {
		if (segments.length && segments[0].path === path) {
			// consume all URL segments
			return { consumed: segments };
		}
		return null;
	}
	return {
		matcher: prefixMatcher,
		loadComponent: () => import('@foundation/articles/ui').then((m) => m.ArticleRootListComponent),
		data: { sourceFolderId, listName, segmentPath: path, listDescription, commentTitle },
	};
}

const userRootPath = 'host';

export const defaultAppRoutes: Route[] = [
	buildAdminRoute([
		{
			path: 'rumble-studio-migration',
			canMatch: [adminAndVerifiedGuard],
			loadComponent: () => import('@spoken/pages').then((m) => m.AdminRumbleMigrationPageComponent),
			data: { adminNavigationLabel: 'Rumble migration' },
		},
	]),
	// root path (host)
	{
		// while connected as host (can lead to the dashboard, the auth pages)
		path: userRootPath,
		loadComponent: () => import('@foundation/workspace/pages').then((m) => m.DefaultWorkspacePageComponent),
		canActivateChild: [
			(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
				// looking for auth options ? => ok
				if (state.url.startsWith('/' + userRootPath + '/auth')) return true;

				// connected ? => ok
				const authTokensRepository = inject(AuthTokensRepository);
				if (authTokensRepository.getCurrentAuthToken()) return true;

				// not connected ? => redirect to login page with the return url
				const router = inject(Router);
				router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });

				return false;
			},
		],
		children: [
			// dashboard
			{
				// while connected as host to manage the product (profile, domains, files, ...)
				path: 'dashboard',
				// component: DashboardHostPageComponent, // with the dashboard header
				loadComponent: () => import('@spoken/pages').then((m) => m.DashboardHostPageComponent),
				children: [
					// ''
					{
						path: '',
						loadComponent: () => import('@spoken/pages').then((m) => m.DashboardHomePageComponent),
					},
					// assistant
					{
						path: 'assistant',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/mcp/ui').then((m) => m.McpAssistantComponent),
							},
							{
								path: ':articleId',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleDisplayerPageComponent),
								data: {
									mcpRouteDetails: {
										resourceKind: 'article',
										resourceIdParam: 'id',
										fullRoute: '/host/dashboard/assistant/:articleId',
										description: 'Route to open and display an assistant article',
									},
								},
							},
						],
					},
					// profile
					{
						path: 'profile',
						loadComponent: () => import('@spoken/pages').then((m) => m.ProfilePageComponent),
					},
					// billing
					{
						path: 'billing',
						loadComponent: () => import('@spoken/pages').then((m) => m.BillingPageComponent),
					},
					// teams
					{
						path: 'teams',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/teams/pages').then((m) => m.TeamListPageComponent),
							},
							{
								path: ':teamId/builder',
								loadComponent: () => import('@foundation/teams/pages').then((m) => m.TeamBuilderPageComponent),
								data: { hideNavBar: true },
							},
						],
					},
					// domains
					{
						path: 'domains',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/domains/pages').then((m) => m.DomainListPageComponent),
							},
							{
								path: ':domainId/builder',
								loadComponent: () => import('@foundation/domains/pages').then((m) => m.DomainBuilderPageComponent),
								data: { hideNavBar: true },
							},
						],
					},
					// pages
					{
						path: 'pages',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/pages/pages').then((m) => m.PageListPageComponent),
							},
							{
								path: ':pageId/builder',
								loadComponent: () => import('@foundation/pages/pages').then((m) => m.PageBuilderPageComponent),
								data: { hideNavBar: true },
							},
						],
					},
					// settings
					{
						path: 'settings',
						loadComponent: () => import('@spoken/pages').then((m) => m.ProfilePageComponent),
					},
					// interviews
					{
						path: 'interviews',
						children: [
							{
								path: '',
								loadComponent: () => import('@spoken/interviews/pages').then((m) => m.InterviewListPageComponent),
							},
							{
								path: ':interviewId/builder',
								loadComponent: () => import('@spoken/interviews/builder').then((m) => m.InterviewBuilderPageComponent),
								data: { hideNavBar: true },
								resolve: { interview: InterviewResolver },
							},
							{
								path: ':interviewId/json',
								loadComponent: () => import('@spoken/interviews/pages').then((m) => m.InterviewJsonPageComponent),
							},
						],
					},
					// forms
					{
						path: 'forms',
						children: [
							{
								path: '',
								loadComponent: () => import('@spoken/forms/pages').then((m) => m.FormListPageComponent),
							},
							{
								path: ':formId/builder',
								loadComponent: () => import('@spoken/forms/pages').then((m) => m.FormBuilderPageComponent),
								data: { hideNavBar: true,enableRouterSync: true },
							},
						],
					},
					// files
					{
						path: 'files',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/files/pages').then((m) => m.FileListPageComponent),
							},
							{
								path: '**',
								redirectTo: '',
							},
						],
					},
					// explorer
					{
						path: 'explorer',
						loadComponent: () => import('./pages/dashboard-explorer-page/dashboard-explorer-page.component').then((m) => m.DashboardExplorerPageComponent),
					},
					// ds
					{
						path: 'design-systems',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/design-systems/pages').then((m) => m.DesignSystemListPageComponent),
							},
							{
								path: ':designSystemId/builder',
								loadComponent: () => import('@foundation/design-systems/pages').then((m) => m.DesignSystemBuilderPageComponent),
								data: { hideNavBar: true },
								// resolve: { designSystem: import('@foundation/design-systems/ui').then((m) => m.DesignSystemResolver) },
							},
							// {
							// 	path: ':designSystemId/json',
							// 	component: DesignSystemJsonPageComponent,
							// },
							// {
							// 	path: '**',
							// 	redirectTo: '',
							// },
						],
					},
					// backlog / build-in-public
					{
						path: 'backlog',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/backlogs/pages').then((m) => m.BacklogListPageComponent),
							},
							// {
							// 	path: ':backlogId/builder',
							// 	loadComponent: () => import('@foundation/backlogs/ui').then((m) => m.BacklogTableComponent),
							// },
							// {
							// 	path: ':designSystemId/json',
							// 	component: DesignSystemJsonPageComponent,
							// },
							// {
							// 	path: '**',
							// 	redirectTo: '',
							// },
						],
					},
					// article
					{
						path: 'articles',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleListPageComponent),
							},
							{
								path: ':articleId/builder',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleBuilderPageComponent),
								data: { hideNavBar: true },
							},
							{
								path: ':articleId',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleDisplayerPageComponent),
								data: {
									commentTitle: 'Comments',
								},
							},
						],
					},

					// mix
					{
						path: 'mixes',
						children: [
							{
								path: '',
								loadComponent: () => import('@spoken/mixes/pages').then((m) => m.MixListPageComponent),
							},
							{
								path: ':mixId/builder',
								loadComponent: () => import('@spoken/mixes/pages').then((m) => m.MixBuilderPageComponent),
								data: { hideNavBar: true },
							},
						],
					},
					// collections
					{
						path: 'collections',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/collections/pages').then((m) => m.CollectionListPageComponent),
							},
							{
								path: ':collectionId/builder',
								loadComponent: () => import('@foundation/collections/pages').then((m) => m.CollectionBuilderPageComponent),
								data: { hideNavBar: true },
							},
						],
					},
					// support
					{
						path: 'support',
						children: [
							{
								path: '',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.SupportListPageComponent),
							},
							{
								path: ':articleId',
								loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleDisplayerPageComponent),
							},
						],
					},
					// tools
					{
						path: 'tools',
						loadComponent: () => import('@spoken/pages').then((m) => m.ToolsPageComponent),
					},
					// real-time demo
					{
						path: 'realtime-demo',
						loadComponent: () => import('@foundation/realtime').then((m) => m.RealtimeDemoComponent),
					},
					{
						path: 'flows',
						children: [
							{
								path: '',
								// component: FlowListPageComponent,
								loadComponent: () => import('@edf/flows/pages').then((m) => m.FlowListPageComponent),
							},
							// {
							// 	path: ':flowId/builder',
							// 	component: FlowBuilderPageComponent,
							// 	data: { hideNavBar: true },
							// 	resolve: { flow: FlowResolver },
							// },
							{
								path: ':flowId/json',
								// component: FlowJsonPageComponent,
								loadComponent: () => import('@edf/flows/pages').then((m) => m.FlowJsonPageComponent),
							},
						],
					},
					// **
					{
						path: '**',
						redirectTo: '',
					},
				],
			},

			// Rumble Studio data export tools
			{
				path: 'rumblestudio/interviews',
				redirectTo: 'rumblestudio',
			},
			{
				path: 'rumblestudio/designsystem',
				redirectTo: 'rumblestudio',
			},
			{
				path: 'rumblestudio',
				loadComponent: () => import('@spoken/pages').then((m) => m.RumbleStudioPageComponent),
			},

			// **
			{
				path: '**',
				redirectTo: 'dashboard',
			},
		],
	},

	// {
	// 	path: '**',
	// 	component: MaxiProxyComponent,
	// },
	{
		path: 'interview/:interviewId',
		loadComponent: () => import('@spoken/interviews/pages').then((m) => m.InterviewDisplayPageComponent),
		data: { hideFloatingChat: true },
	},
	// Public Form Runner
	{
		path: 'f/:formId',
		loadComponent: () => import('@spoken/forms/pages').then((m) => m.FormRunnerPageComponent),
		data: { hideNavBar: true, hideFloatingChat: true },
	},
	// Public Collection page — the human-readable page a podcast RSS feed's
	// <link> points to (as opposed to the raw XML endpoint). Same pattern as
	// the public form runner above: no auth, no app chrome.
	{
		path: 'c/:collectionId',
		loadComponent: () => import('@foundation/collections/pages').then((m) => m.CollectionDisplayPageComponent),
		data: { hideNavBar: true, hideFloatingChat: true },
	},
	// Single episode of a public collection — the RSS feed's per-item <link>.
	{
		path: 'c/:collectionId/:itemId',
		loadComponent: () => import('@foundation/collections/pages').then((m) => m.CollectionItemDisplayPageComponent),
		data: { hideNavBar: true, hideFloatingChat: true },
	},
	// article alone (no header)
	// {
	// 	path: 'article/:articleId',
	// 	loadComponent: () => import('@foundation/articles/pages').then((m) => m.ArticleDisplayerPageComponent),
	// 	data: {
	// 		commentTitle: 'Comments',
	// 	},
	// },
	{
		path: '',
		loadComponent: () => import('@spoken/pages').then((m) => m.LandingPageRouterPageComponent),
		children: [
			// pricing
			{
				path: 'pricing',
				loadComponent: () => import('@spoken/pages').then((m) => m.PricingPageComponent),
			},
			// faq
			{
				path: 'faq',
				loadComponent: () => import('@spoken/pages').then((m) => m.FaqPageComponent),
			},
			// team
			{
				path: 'team',
				loadComponent: () => import('@spoken/pages').then((m) => m.TeamPageComponent),
			},
			// terms of service
			{
				path: 'terms-of-service',
				loadComponent: () => import('@spoken/pages').then((m) => m.ToSPageComponent),
			},
			// privacy policy
			{
				path: 'privacy-policy',
				loadComponent: () => import('@spoken/pages').then((m) => m.PrivacyPolicyPageComponent),
			},
			// blog/articles
			generateArticleRoutes('blog', environment.articles.folders.blog, 'Blog', 'Insights, tutorials, and stories on podcasting, audio production, digital media and the company history.', 'Comments'),
			generateArticleRoutes('use-cases', environment.articles.folders.useCases, 'Explore our use cases', 'Discover real-world scenarios where our platform shines—browse practical examples, best practices, and application guides that show you exactly how to leverage our features for your needs.'),

			{
				path: 'features',
				loadComponent: () => import('@spoken/pages').then((m) => m.FeaturesPageComponent),
			},
			{
				path: 'support',
				loadComponent: () => import('@spoken/pages').then((m) => m.SupportPageComponent),
				children: [generateArticleRoutes('articles', environment.articles.folders.support.articles, 'Support knowledge base', 'Browse how-to guides, tutorials and frequently asked questions to quickly find solutions and tips for using our platform.')],
			},
			{
				path: 'auth',
				loadComponent: () => import('@foundation/auth/pages').then((m) => m.AuthOptionsPageComponent),
				children: [
					{
						path: 'login',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.LoginPageComponent),
					},
					{
						path: 'reset',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.ResetRequestPageComponent),
					},
					{
						path: 'reset-claim',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.ResetClaimPageComponent),
					},
					{
						path: 'verify-email-claim',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.VerifyEmailClaimPageComponent),
					},
					{
						path: 'change-email-claim',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.ChangeEmailClaimPageComponent),
					},
					{
						path: 'register',
						loadComponent: () => import('@foundation/auth/pages').then((m) => m.RegisterPageComponent),
					},
					{
						path: '**',
						redirectTo: 'login',
					},
				],
			},
			{
				path: '',
				loadComponent: () => import('@spoken/pages').then((m) => m.SpokenSimpleLandingPageComponent),
			},
			{
				path: 'landing-v2',
				loadComponent: () => import('@spoken/pages').then((m) => m.LandingPageV2Component),
			},
			{
				path: '**',
				redirectTo: '',
			},
		],
	},

	{
		path: '**',
		redirectTo: '/auth/login',
	},
];

export const cnamedRoutes: Route[] = [
	{
		matcher: (segments: UrlSegment[], group: UrlSegmentGroup, route: Route) => {
			console.log('[cnamedRoutes] matcher', { segments, group, route });
			const currentUrl = window.location.href;
			if (currentUrl.includes('cnamed-debug')) {
				return { consumed: segments };
			}
			return null;
		},
		loadComponent: () => import('./pages/cnamed-debug-page/cnamed-debug-page.component').then((m) => m.CnamedDebugPageComponent),
	},
	{
		path: '**',
		loadComponent: () => import('./pages/cnamed-page/cnamed-page.component').then((m) => m.CnamedPageComponent),
		data: {
			hideNavBar: true,
			hideFloatingChat: true,
		},
	},
];

export const appRoutes: Route[] = cnamed ? cnamedRoutes : defaultAppRoutes;
// export const appRoutes: Route[] = defaultAppRoutes;

// data: {
// 	isSomething: true,
// },
// resolve: {
// 	resolveFoo: (
// 		//
// 		activatedRouteSnapshot: ActivatedRouteSnapshot,
// 		state: RouterStateSnapshot
// 	) => {
// 		console.log('[appRoutes](resolveFoo)', activatedRouteSnapshot, state);
// 		console.log('[appRoutes](resolveFoo)', { macroBlockId: activatedRouteSnapshot.params['macroBlockId'] });
// 		return 'My resolved data';
// 	},
// },
